<template>
  <v-row class="my-0">
    <v-col class="ma-0 ps-0 ms-n2">
      <Draggable
        ghost-class="ghost-item"
        handle=".option-drag-control"
        :list="options"
      >
        <template v-for="(option, index) in options">
          <div
            class="d-flex align-center justify-space-between checklist-item-option"
            :key="`option-${index}`"
          >
            <v-icon
              class="option-drag-control ma-0 pa-0 me-n4 show-hover mt-n7"
              style="cursor: grab"
              size="24"
              color="blue-grey"
              >mdi-drag-vertical</v-icon
            >
            <v-text-field
              class="text-option-input pa-0 ma-0
            ms-4"
              style="min-width: 480px"
              required
              dense
              outlined
              :autofocus="active"
              :label="$t('checklist.option_count', { count: index + 1 })"
              :data-testid="`routineChecklist-input-${type}`"
              :disabled="disabled"
              :value="option.name"
              :placeholder="
                `${$t('checklist.option_count', { count: index + 1 })}*`
              "
              :rules="rules"
              @keydown.enter="handleAddOption"
              @input="$emit('change', { data: { name: $event }, index })"
            >
              <template v-slot:prepend>
                <v-icon color="blue-grey" size="24">{{ icon }}</v-icon>
              </template>

              <template v-slot:append-outer>
                <div class="d-flex align-center">
                  <v-menu
                    v-if="!hideNotificationAndFindingButtons"
                    max-width="300"
                    offset-y
                    open-on-hover
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="me-3"
                        :color="
                          option.hasNotification ? 'primary' : 'blue-grey'
                        "
                        @click="
                          $emit('change', {
                            data: { hasNotification: !option.hasNotification },
                            index,
                          })
                        "
                        >{{
                          option.hasNotification
                            ? 'mdi-bell'
                            : 'mdi-bell-outline'
                        }}</v-icon
                      >
                    </template>

                    <div
                      class="px-3 py-2 text-center d-flex"
                      style="background-color: rgba(45, 55, 72, 0.9);"
                    >
                      <p class="text-body-2 white--text pa-0 ma-0">
                        {{ $t('checklist.toggle_notification') }}
                      </p>
                      <v-icon
                        class="ml-2"
                        :color="'primary'"
                        @click="goToHelpCenter"
                        >mdi-help-circle-outline</v-icon
                      >
                    </div>
                  </v-menu>

                  <v-menu
                    v-if="!hideNotificationAndFindingButtons"
                    max-width="300"
                    offset-y
                    open-on-hover
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        class="me-3"
                        :color="
                          option.requiresFinding ? 'primary' : 'blue-grey'
                        "
                        @click="
                          $emit('change', {
                            data: { requiresFinding: !option.requiresFinding },
                            index,
                          })
                        "
                      >
                        {{
                          option.requiresFinding
                            ? 'mdi-toy-brick-search'
                            : 'mdi-toy-brick-search-outline'
                        }}
                      </v-icon>
                    </template>

                    <div
                      class="px-3 py-2 text-center d-flex"
                      style="background-color: rgba(45, 55, 72, 0.9);"
                    >
                      <p class="text-body-2 white--text pa-0 ma-0">
                        {{ $t('checklist.require_finding_creation') }}
                      </p>
                      <v-icon
                        class="ml-2"
                        :color="'primary'"
                        @click="goToHelpCenter"
                        >mdi-help-circle-outline</v-icon
                      >
                    </div>
                  </v-menu>

                  <v-tooltip top v-if="options.length > 1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="blue-grey"
                        @click="$emit('remove', index)"
                        >mdi-close</v-icon
                      >
                    </template>
                    <span>{{ $t('checklist.remove_option') }}</span>
                  </v-tooltip>
                </div></template
              >
            </v-text-field>
          </div>
        </template>
      </Draggable>
      <Button
        class="mt-n1 ms-6"
        color="primary"
        data-testid="routineChecklist-button-addOption"
        icon="mdi-plus"
        plain
        size="small"
        :disabled="disabled"
        :text="$t('checklist.add_option')"
        @click="handleAddOption"
      />
    </v-col>
  </v-row>
</template>
<script>
import Draggable from 'vuedraggable'

import Button from '@/components/UI/Button'

export default {
  name: 'Options',

  components: {
    Button,
    Draggable,
  },

  props: {
    options: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    active: Boolean,
    disabled: Boolean,
    hideNotificationAndFindingButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      rules: [
        value =>
          !!value.trim() || this.$t('validation.form.this_field_is_required'),
      ],
    }
  },

  computed: {
    icon() {
      switch (this.type) {
        case 'checkboxes':
          return 'mdi-checkbox-blank-outline'
        case 'radios':
          return 'mdi-radiobox-blank'
        case 'select':
          return ''
        default:
          return ''
      }
    },
  },

  methods: {
    handleAddOption(e) {
      e.preventDefault()

      this.$emit('add')
    },

    goToHelpCenter() {
      window.open(
        `${this.$constants.urls.helpCenter}/en/article/routines-1wc17in/#6-enhanced-checklist-rules`,
        '_blank'
      )
    },
  },
}
</script>

<style lang="scss">
.ghost-item {
  opacity: 0.5;
}

.checklist-item-option:hover {
  .show-hover {
    visibility: visible !important;
  }
}
.show-hover {
  visibility: hidden !important;
}
</style>
